import React, { useState, useEffect } from 'react';
import './PerformanceDashboard.css';

interface PerformanceMetrics {
  salesMetrics: {
    totalSales: number;
    transactionCount: number;
    averageTicket: number;
    itemsSold: number;
    upsells: number;
    returns: number;
    voids: number;
  };
  serviceMetrics: {
    customerCount: number;
    averageServiceTime: number;
    customerRating: number;
    complaints: number;
    compliments: number;
  };
  productivityMetrics: {
    tasksCompleted: number;
    ordersProcessed: number;
    efficiency: number;
    accuracy: number;
    speed: number;
  };
  complianceMetrics: {
    clockInOnTime: boolean;
    breaksTaken: number;
    uniformCompliance: boolean;
    safetyIncidents: number;
  };
}

interface EmployeePerformance {
  employeeId: string;
  storeId: string;
  workDate: string;
  metrics: PerformanceMetrics;
  calculatedAt: string;
  employee: {
    firstName: string;
    lastName: string;
    employeeNumber: string;
  };
  store: {
    name: string;
  };
}

interface PerformanceScore {
  employeeId: string;
  storeId: string;
  dateRange: {
    start: string;
    end: string;
  };
  scoreComponents: {
    sales: {
      averageSales: number;
      averageTicket: number;
      averageTransactions: number;
      score: number;
    };
    service: {
      averageServiceTime: number;
      averageRating: number;
      totalComplaints: number;
      score: number;
    };
    productivity: {
      averageEfficiency: number;
      averageAccuracy: number;
      averageSpeed: number;
      score: number;
    };
    compliance: {
      onTimeRate: number;
      averageBreaksTaken: number;
      uniformComplianceRate: number;
      totalSafetyIncidents: number;
      score: number;
    };
  };
  overallScore: number;
  trend: 'improving' | 'stable' | 'declining';
  recommendations: Array<{
    category: string;
    priority: 'high' | 'medium' | 'low';
    message: string;
  }>;
  calculatedAt: string;
}

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  employeeNumber: string;
}

interface PerformanceDashboardProps {
  storeId: string;
  enterpriseId: string;
}

export const PerformanceDashboard: React.FC<PerformanceDashboardProps> = ({
  storeId,
  enterpriseId
}) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [performanceData, setPerformanceData] = useState<EmployeePerformance[]>([]);
  const [performanceScores, setPerformanceScores] = useState<Record<string, PerformanceScore>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [dateRange, setDateRange] = useState({
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate()
  });
  const [viewMode, setViewMode] = useState<'overview' | 'detailed' | 'trends'>('overview');
  const [sortBy, setSortBy] = useState<'score' | 'sales' | 'service' | 'productivity'>('score');

  function getDefaultStartDate() {
    const date = new Date();
    date.setDate(date.getDate() - 30); // Last 30 days
    return date.toISOString().split('T')[0];
  }

  function getDefaultEndDate() {
    return new Date().toISOString().split('T')[0];
  }

  useEffect(() => {
    fetchEmployees();
  }, [storeId]);

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      fetchPerformanceData();
    }
  }, [dateRange, storeId, selectedEmployee]);

  const fetchEmployees = async () => {
    try {
      const params = new URLSearchParams({
        storeId: storeId,
        status: 'active',
        limit: '100'
      });

      const response = await fetch(`/api/employees?${params}`);
      if (!response.ok) throw new Error('Failed to fetch employees');

      const data = await response.json();
      setEmployees(data.employees);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  const fetchPerformanceData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch performance metrics for all employees or selected employee
      const employeesToFetch = selectedEmployee ? [selectedEmployee] : employees.map(e => e.id);
      
      const performancePromises = employeesToFetch.map(async (employeeId) => {
        const response = await fetch(
          `/api/employees/${employeeId}/performance?storeId=${storeId}&startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`
        );
        
        if (response.ok) {
          return await response.json();
        }
        return null;
      });

      const scores = await Promise.all(performancePromises);
      const scoresMap: Record<string, PerformanceScore> = {};
      
      scores.forEach((score) => {
        if (score) {
          scoresMap[score.employeeId] = score;
        }
      });

      setPerformanceScores(scoresMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return '#28a745'; // Green
    if (score >= 60) return '#ffc107'; // Yellow
    if (score >= 40) return '#fd7e14'; // Orange
    return '#dc3545'; // Red
  };

  const getScoreGrade = (score: number) => {
    if (score >= 90) return 'A';
    if (score >= 80) return 'B';
    if (score >= 70) return 'C';
    if (score >= 60) return 'D';
    return 'F';
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving': return '[IMPROVING]';
      case 'declining': return '[DECLINING]';
      default: return '[STABLE]';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return '#dc3545';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return '#6c757d';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const getSortedPerformanceData = () => {
    const data = Object.values(performanceScores);
    
    switch (sortBy) {
      case 'score':
        return data.sort((a, b) => b.overallScore - a.overallScore);
      case 'sales':
        return data.sort((a, b) => b.scoreComponents.sales.score - a.scoreComponents.sales.score);
      case 'service':
        return data.sort((a, b) => b.scoreComponents.service.score - a.scoreComponents.service.score);
      case 'productivity':
        return data.sort((a, b) => b.scoreComponents.productivity.score - a.scoreComponents.productivity.score);
      default:
        return data;
    }
  };

  const calculateTeamAverages = () => {
    const scores = Object.values(performanceScores);
    if (scores.length === 0) return null;

    return {
      overallScore: scores.reduce((sum, s) => sum + s.overallScore, 0) / scores.length,
      salesScore: scores.reduce((sum, s) => sum + s.scoreComponents.sales.score, 0) / scores.length,
      serviceScore: scores.reduce((sum, s) => sum + s.scoreComponents.service.score, 0) / scores.length,
      productivityScore: scores.reduce((sum, s) => sum + s.scoreComponents.productivity.score, 0) / scores.length,
      complianceScore: scores.reduce((sum, s) => sum + s.scoreComponents.compliance.score, 0) / scores.length,
    };
  };

  const getEmployeeName = (employeeId: string) => {
    const employee = employees.find(e => e.id === employeeId);
    return employee ? `${employee.firstName} ${employee.lastName}` : 'Unknown Employee';
  };

  if (loading) return <div className="loading">Loading performance data...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  const teamAverages = calculateTeamAverages();
  const sortedPerformance = getSortedPerformanceData();

  return (
    <div className="performance-dashboard">
      <div className="dashboard-header">
        <h2>Employee Performance Dashboard</h2>
        <div className="header-actions">
          <button 
            className="btn btn-secondary"
            onClick={fetchPerformanceData}
            disabled={loading}
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="performance-controls">
        <div className="date-controls">
          <div className="form-group">
            <label>Start Date:</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>End Date:</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
              className="form-input"
            />
          </div>
        </div>

        <div className="filter-controls">
          <div className="form-group">
            <label>Employee:</label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="form-input"
            >
              <option value="">All Employees</option>
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.firstName} {employee.lastName} ({employee.employeeNumber})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>View:</label>
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value as any)}
              className="form-input"
            >
              <option value="overview">Overview</option>
              <option value="detailed">Detailed</option>
              <option value="trends">Trends</option>
            </select>
          </div>

          <div className="form-group">
            <label>Sort By:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="form-input"
            >
              <option value="score">Overall Score</option>
              <option value="sales">Sales Performance</option>
              <option value="service">Service Quality</option>
              <option value="productivity">Productivity</option>
            </select>
          </div>
        </div>
      </div>

      {teamAverages && (
        <div className="team-summary">
          <h3>Team Performance Summary</h3>
          <div className="summary-cards">
            <div className="summary-card">
              <div className="card-header">
                <h4>Overall Score</h4>
                <span className="score-badge" style={{ backgroundColor: getScoreColor(teamAverages.overallScore) }}>
                  {teamAverages.overallScore.toFixed(1)}
                </span>
              </div>
              <div className="card-subtitle">Team Average</div>
            </div>

            <div className="summary-card">
              <div className="card-header">
                <h4>Sales</h4>
                <span className="score-value">{teamAverages.salesScore.toFixed(1)}</span>
              </div>
            </div>

            <div className="summary-card">
              <div className="card-header">
                <h4>Service</h4>
                <span className="score-value">{teamAverages.serviceScore.toFixed(1)}</span>
              </div>
            </div>

            <div className="summary-card">
              <div className="card-header">
                <h4>Productivity</h4>
                <span className="score-value">{teamAverages.productivityScore.toFixed(1)}</span>
              </div>
            </div>

            <div className="summary-card">
              <div className="card-header">
                <h4>Compliance</h4>
                <span className="score-value">{teamAverages.complianceScore.toFixed(1)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="performance-content">
        {viewMode === 'overview' && (
          <div className="overview-grid">
            {sortedPerformance.map((performance) => (
              <div key={performance.employeeId} className="performance-card">
                <div className="card-header">
                  <div className="employee-info">
                    <h4>{getEmployeeName(performance.employeeId)}</h4>
                    <span className="date-range">
                      {new Date(performance.dateRange.start).toLocaleDateString()} - {new Date(performance.dateRange.end).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="overall-score">
                    <div 
                      className="score-circle"
                      style={{ backgroundColor: getScoreColor(performance.overallScore) }}
                    >
                      {getScoreGrade(performance.overallScore)}
                    </div>
                    <span className="score-number">{performance.overallScore.toFixed(1)}</span>
                  </div>
                </div>

                <div className="score-breakdown">
                  <div className="score-item">
                    <span className="score-label">Sales</span>
                    <div className="score-bar">
                      <div 
                        className="score-fill"
                        style={{ 
                          width: `${performance.scoreComponents.sales.score}%`,
                          backgroundColor: getScoreColor(performance.scoreComponents.sales.score)
                        }}
                      ></div>
                    </div>
                    <span className="score-value">{performance.scoreComponents.sales.score.toFixed(1)}</span>
                  </div>

                  <div className="score-item">
                    <span className="score-label">Service</span>
                    <div className="score-bar">
                      <div 
                        className="score-fill"
                        style={{ 
                          width: `${performance.scoreComponents.service.score}%`,
                          backgroundColor: getScoreColor(performance.scoreComponents.service.score)
                        }}
                      ></div>
                    </div>
                    <span className="score-value">{performance.scoreComponents.service.score.toFixed(1)}</span>
                  </div>

                  <div className="score-item">
                    <span className="score-label">Productivity</span>
                    <div className="score-bar">
                      <div 
                        className="score-fill"
                        style={{ 
                          width: `${performance.scoreComponents.productivity.score}%`,
                          backgroundColor: getScoreColor(performance.scoreComponents.productivity.score)
                        }}
                      ></div>
                    </div>
                    <span className="score-value">{performance.scoreComponents.productivity.score.toFixed(1)}</span>
                  </div>

                  <div className="score-item">
                    <span className="score-label">Compliance</span>
                    <div className="score-bar">
                      <div 
                        className="score-fill"
                        style={{ 
                          width: `${performance.scoreComponents.compliance.score}%`,
                          backgroundColor: getScoreColor(performance.scoreComponents.compliance.score)
                        }}
                      ></div>
                    </div>
                    <span className="score-value">{performance.scoreComponents.compliance.score.toFixed(1)}</span>
                  </div>
                </div>

                <div className="card-footer">
                  <div className="trend">
                    <span className="trend-icon">{getTrendIcon(performance.trend)}</span>
                    <span className="trend-text">{performance.trend}</span>
                  </div>
                  <div className="recommendations-count">
                    {performance.recommendations.length} recommendations
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {viewMode === 'detailed' && selectedEmployee && performanceScores[selectedEmployee] && (
          <div className="detailed-view">
            <div className="detailed-header">
              <h3>{getEmployeeName(selectedEmployee)} - Detailed Performance</h3>
            </div>

            <div className="detailed-metrics">
              <div className="metrics-section">
                <h4>Sales Performance</h4>
                <div className="metrics-grid">
                  <div className="metric-item">
                    <label>Average Sales:</label>
                    <span>{formatCurrency(performanceScores[selectedEmployee].scoreComponents.sales.averageSales)}</span>
                  </div>
                  <div className="metric-item">
                    <label>Average Ticket:</label>
                    <span>{formatCurrency(performanceScores[selectedEmployee].scoreComponents.sales.averageTicket)}</span>
                  </div>
                  <div className="metric-item">
                    <label>Average Transactions:</label>
                    <span>{performanceScores[selectedEmployee].scoreComponents.sales.averageTransactions.toFixed(1)}</span>
                  </div>
                </div>
              </div>

              <div className="metrics-section">
                <h4>Service Quality</h4>
                <div className="metrics-grid">
                  <div className="metric-item">
                    <label>Service Time:</label>
                    <span>{performanceScores[selectedEmployee].scoreComponents.service.averageServiceTime.toFixed(1)} min</span>
                  </div>
                  <div className="metric-item">
                    <label>Customer Rating:</label>
                    <span>{performanceScores[selectedEmployee].scoreComponents.service.averageRating.toFixed(1)}/5</span>
                  </div>
                  <div className="metric-item">
                    <label>Complaints:</label>
                    <span>{performanceScores[selectedEmployee].scoreComponents.service.totalComplaints}</span>
                  </div>
                </div>
              </div>

              <div className="metrics-section">
                <h4>Productivity</h4>
                <div className="metrics-grid">
                  <div className="metric-item">
                    <label>Efficiency:</label>
                    <span>{formatPercentage(performanceScores[selectedEmployee].scoreComponents.productivity.averageEfficiency)}</span>
                  </div>
                  <div className="metric-item">
                    <label>Accuracy:</label>
                    <span>{formatPercentage(performanceScores[selectedEmployee].scoreComponents.productivity.averageAccuracy)}</span>
                  </div>
                  <div className="metric-item">
                    <label>Speed:</label>
                    <span>{formatPercentage(performanceScores[selectedEmployee].scoreComponents.productivity.averageSpeed)}</span>
                  </div>
                </div>
              </div>

              <div className="metrics-section">
                <h4>Compliance</h4>
                <div className="metrics-grid">
                  <div className="metric-item">
                    <label>On-Time Rate:</label>
                    <span>{formatPercentage(performanceScores[selectedEmployee].scoreComponents.compliance.onTimeRate * 100)}</span>
                  </div>
                  <div className="metric-item">
                    <label>Uniform Compliance:</label>
                    <span>{formatPercentage(performanceScores[selectedEmployee].scoreComponents.compliance.uniformComplianceRate * 100)}</span>
                  </div>
                  <div className="metric-item">
                    <label>Safety Incidents:</label>
                    <span>{performanceScores[selectedEmployee].scoreComponents.compliance.totalSafetyIncidents}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="recommendations-section">
              <h4>Recommendations</h4>
              <div className="recommendations-list">
                {performanceScores[selectedEmployee].recommendations.map((rec, index) => (
                  <div key={index} className="recommendation-item">
                    <div className="recommendation-header">
                      <span 
                        className="priority-badge"
                        style={{ backgroundColor: getPriorityColor(rec.priority) }}
                      >
                        {rec.priority.toUpperCase()}
                      </span>
                      <span className="category">{rec.category.toUpperCase()}</span>
                    </div>
                    <p className="recommendation-message">{rec.message}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {Object.keys(performanceScores).length === 0 && (
          <div className="no-data">
            <h3>No Performance Data</h3>
            <p>No performance data available for the selected period. Performance data is generated from POS system integration.</p>
          </div>
        )}
      </div>
    </div>
  );
};